import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ChartData, ChartOptions, CoreScaleOptions, Scale } from 'chart.js';
import { getYAxisTicksLabel } from '@fi-shared/checklist/fi-checklist2-report/components/checklist-history-report/checklist-details/unit-performance-metrics/history-checklist-chart-settings';
import { AppTextService } from '@fi-shared/app-text';
import {
    checklistUnitBenchmarkChartExternalTooltipHandler,
    getDeviationCheckpointsBackgroundFn,
    getDeviationCheckpointsPercentage,
    getOkCheckpointsBackgroundFn,
    getOkCheckpointsBorderRadiusFn,
    getOkCheckpointsPercentage,
    getUnitBenchmarkChartLabels,
    UnitBenchmarkChartBarBorderRadius,
    UnitBenchmarkChartMaxBarThickness,
} from '../unit-benchmark-chart/unit-benchmark-chart-settings';
import { IChecklistBenchmarkViewData } from '../checklists-benchmark/checklists-benchmark.component';
import { debounce, takeUntil } from 'rxjs/operators';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { BehaviorSubject, interval, Subject } from 'rxjs';
import { cloneDeep } from '@fi-shared/utils';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-checklist-history-unit-benchmark-chart',
    templateUrl: './unit-benchmark-chart.component.html',
    styleUrls: ['./unit-benchmark-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-history-unit-benchmark-chart' },
    standalone: false,
})
export class FiChecklistHistoryUnitBenchmarkChartComponent implements OnInit {
    private _checklistData: IChecklistBenchmarkViewData;
    @Input() set checklistData(value: IChecklistBenchmarkViewData) {
        this._checklistData = cloneDeep(value);
        this.buildCustomChart();
    }
    @Input({ required: true }) objectType: ObjectTypes;
    @Input() checklistName: string;
    @ViewChild('checklistReportChartElRef', { static: true }) checklistReportChartElRef: ElementRef<HTMLCanvasElement>;
    isChartShown$ = new BehaviorSubject<boolean>(true);

    chartData: ChartData;
    chartOptions: ChartOptions<'bar'> = {
        animation: { duration: 1500 },
        interaction: { mode: 'index', intersect: false },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                enabled: false,
                position: 'average',
                external: (context) => {
                    return checklistUnitBenchmarkChartExternalTooltipHandler(
                        context,
                        this._checklistData,
                        this.appTextService,
                        this.objectType,
                        this.checklistName,
                    );
                },
            },
            legend: { display: false },
        },
        scales: {
            y: {
                suggestedMin: 0,
                suggestedMax: 100,
                beginAtZero: true,
                title: { display: true },
                ticks: { callback: getYAxisTicksLabel },
                stacked: true,
            },
            x: {
                afterSetDimensions(scale: Scale<CoreScaleOptions>) {
                    const labels: unknown[] = scale.chart.data.labels;
                    const tickWidth: number = Math.round(scale.maxWidth / labels.length);
                    labels.forEach((label: unknown, i: number) => {
                        if (typeof label === 'string') {
                            const span: HTMLSpanElement = document.createElement('span');
                            span.innerText = label;
                            span.style.opacity = '0';
                            document.body.appendChild(span);
                            const spanWidth: number = span.offsetWidth;
                            document.body.removeChild(span);
                            if (spanWidth > tickWidth) {
                                const tickToLabelRatio: number = 1 - tickWidth / spanWidth;
                                const extraLabelChars: number = Math.round(label.length * tickToLabelRatio) + 3;
                                labels[i] = label.substring(0, label.length - extraLabelChars).concat('...');
                            }
                        }
                    });
                },
                stacked: true,
                ticks: {
                    maxRotation: 90,
                    minRotation: 0,
                },
            },
        },
    };

    constructor(
        private appTextService: AppTextService,
        private fiBrowserEventsService: FiBrowserEventsService,
    ) {}

    ngOnInit() {
        this.fiBrowserEventsService
            .getWindowEventSubject$('resize')
            .pipe(
                takeUntil(this.ngOnDestroy$),
                debounce(() => interval(200)),
            )
            .subscribe(() => {
                this.isChartShown$.next(false);
                setTimeout(() => this.isChartShown$.next(true));
            });
    }

    private buildCustomChart() {
        this.chartOptions.scales.y.title.text = this.appTextService.get('common.completed');
        this.chartData = {
            labels: getUnitBenchmarkChartLabels(this._checklistData.Units),
            datasets: [
                {
                    data: this._checklistData.Units.map(getOkCheckpointsPercentage),
                    fill: true,
                    backgroundColor: getOkCheckpointsBackgroundFn,
                    maxBarThickness: UnitBenchmarkChartMaxBarThickness,
                    borderRadius: getOkCheckpointsBorderRadiusFn,
                },
                {
                    data: this._checklistData.Units.map(getDeviationCheckpointsPercentage),
                    fill: true,
                    backgroundColor: getDeviationCheckpointsBackgroundFn,
                    borderRadius: {
                        topLeft: UnitBenchmarkChartBarBorderRadius,
                        topRight: UnitBenchmarkChartBarBorderRadius,
                    },
                    maxBarThickness: UnitBenchmarkChartMaxBarThickness,
                },
            ],
        };
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
